import { type ReactNode, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon, type IconName } from "../components/ui/Icon";
import { Seg } from "../components/ui/Seg";
import { Sheet } from "../components/ui/Sheet";
import { HOST_ORDER, HOSTS, type HostKey } from "./hosts";
import { type Colours, LOOKS } from "./looks";
import type { SettingsResponse } from "../../shared/types";
import { SendSteps } from "./EmailSteps";
import { type LookMode, type RecState, useCopy } from "./parts";
import { BrowserFrame, EmailPreview, initialsOf, PreviewFit, type PreviewWho, RoomPreview, SignupPreview } from "./Previews";
import { MX_PRIORITY, recordName } from "./rules";

// The parts Settings and first-run setup share in Season: fields, looks, previews, record cards and the registrar sheets.

/** A field with its name above and a quiet line under it. */
export function StField({ label, hint, htmlFor, children }: { label: ReactNode; hint?: ReactNode; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint ? <span className="st-hint">{hint}</span> : null}
    </div>
  );
}

/** A field joined to the words after it ("maya" then "@mail.mayatorres.com"). */
export function StJoin({ id, value, onChange, onBlur, suffix, disabled = false, label }: { id?: string; value: string; onChange: (v: string) => void; onBlur?: () => void; suffix: string; disabled?: boolean; label?: string }) {
  return (
    <div className="st-join">
      <input dir="auto" id={id} className="inp" value={value} disabled={disabled} aria-label={label} autoComplete="off" autoCapitalize="none" spellCheck={false} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
      <span>{suffix}</span>
    </div>
  );
}

/** A note in the page's own colours: good, warn or the quiet brass one. */
export function Callout({ tone, icon, children }: { tone?: "good" | "warn"; icon: IconName; children: ReactNode }) {
  return (
    <p className={cx("st-callout", tone)}>
      <Icon name={icon} />
      <span>{children}</span>
    </p>
  );
}

/** The business's name and logo, which live in Flashsuite, and the way there. */
export function BusinessRow({ business, logoUrl }: { business: string; logoUrl: string | null }) {
  const { flashsuite } = useAuth();
  return (
    <GroupedList noLead>
      <li className="k-lr">
        <div className="k-lr-b">
          <span className="st-logo">{logoUrl ? <img src={logoUrl} alt="" /> : initialsOf(business)}</span>
          <span className="k-lr-t">
            <b>{business}</b>
          </span>
          {flashsuite ? (
            <a className="k-link" href={`${flashsuite.url}/business`} target="_blank" rel="noopener noreferrer">
              Change in Flashsuite <Icon name="ext" />
            </a>
          ) : null}
        </div>
      </li>
    </GroupedList>
  );
}

// --- The look ---

const CUSTOM_PICKERS = [
  ["accent", "Accent"],
  ["brass", "Secondary"],
  ["paper", "Page tint"],
] as const;

/** The six looks and the business's own colours as tiles, two to a row so every name fits. */
export function LookTiles({ mode, colours, onPick }: { mode: LookMode; colours: Colours; onPick: (mode: LookMode, colours: Colours) => void }) {
  return (
    <>
      <div className="st-looks">
        {LOOKS.map((l) => (
          <button key={l.id} type="button" className="st-th" aria-pressed={mode === l.id} onClick={() => onPick(l.id, { accent: l.accent, brass: l.brass, paper: l.paper })}>
            <span className="st-sw">
              <i style={{ background: l.accent }} />
              <i style={{ background: l.brass }} />
              <i style={{ background: l.paper }} />
            </span>
            <b>{l.name}</b>
            {mode === l.id ? <Icon name="check" /> : null}
          </button>
        ))}
        <button type="button" className="st-th" aria-pressed={mode === "custom"} onClick={() => onPick("custom", colours)}>
          <span className="st-sw">
            <i style={{ background: colours.accent }} />
            <i style={{ background: colours.brass }} />
            <i style={{ background: colours.paper }} />
          </span>
          <b>Your own colours</b>
          {mode === "custom" ? <Icon name="check" /> : null}
        </button>
      </div>
      {mode === "custom" ? (
        <div className="st-cust">
          {CUSTOM_PICKERS.map(([k, label]) => (
            <label key={k}>
              <span>{label}</span>
              <input type="color" value={colours[k]} onChange={(e) => onPick("custom", { ...colours, [k]: e.target.value })} />
            </label>
          ))}
        </div>
      ) : null}
    </>
  );
}

type PreviewTab = "signup" | "room" | "email";

/** The sign-up page, the room or an email in the colours being chosen: one at a time, behind a switch. */
export function PreviewSwitch({ colours, who }: { colours: Colours; who: PreviewWho }) {
  const [tab, setTab] = useState<PreviewTab>("signup");
  const slug = who.room?.slug ?? "your-room";
  return (
    <div className="st-pvw">
      <Seg
        className="k-seg"
        label="Preview"
        value={tab}
        onChange={setTab}
        options={[
          { value: "signup", label: "Sign-up page" },
          { value: "room", label: "Room" },
          { value: "email", label: "Email" },
        ]}
      />
      <PreviewFit maxHeight={480}>
        {tab === "email" ? (
          <EmailPreview colours={colours} who={who} />
        ) : (
          <BrowserFrame url={`${who.address}/${tab === "room" ? `${slug}/watch` : slug}`}>
            {tab === "room" ? <RoomPreview colours={colours} who={who} dark /> : <SignupPreview colours={colours} who={who} />}
          </BrowserFrame>
        )}
      </PreviewFit>
    </div>
  );
}

// --- Records, as cards ---

/** Found, being checked, or waiting. */
export function RecMark({ state, found = "Found" }: { state: RecState; found?: string }) {
  if (state === "found") {
    return (
      <span className="st-rst ok">
        <Icon name="check" />
        {found}
      </span>
    );
  }
  if (state === "checking") {
    return (
      <span className="st-rst">
        <span className="st-spin" />
        Checking
      </span>
    );
  }
  return (
    <span className="st-rst">
      <span className="st-o" />
      Waiting
    </span>
  );
}

/** A name or value with its Copy: a 44 px button at the end of the row. */
export function CopyLine({ label, value }: { label: string; value: string }) {
  const copy = useCopy();
  return (
    <div className="st-cv">
      <span className="st-cl">{label}</span>
      <span className="st-cval">{value}</span>
      <button type="button" className="k-ib" aria-label={`Copy the ${label.toLowerCase()}`} onClick={() => copy(value)}>
        <Icon name="copy" />
      </button>
    </div>
  );
}

/** One record to add: its type, whether it is there yet, and its name and value to copy. */
export function SeasonRecCard({ type, name, value, state, found }: { type: string; name: string; value: string; state: RecState; found?: string }) {
  return (
    <article className={cx("st-rec", state === "found" && "found")}>
      <header>
        <span className="st-ty">{type}</span>
        {type.toUpperCase() === "MX" ? <span className="st-pri">Priority {MX_PRIORITY}</span> : null}
        <RecMark state={state} found={found} />
      </header>
      <CopyLine label="Name" value={name} />
      <CopyLine label="Value" value={value} />
    </article>
  );
}

// --- Where to paste them ---

/** The records page's two ways out: the registrar's steps, and handing the records to whoever runs the website. */
export function PasteRows({ hostLabel, onHosts, onSend, sentTo }: { hostLabel: string; onHosts: () => void; onSend?: () => void; sentTo?: string | null }) {
  return (
    <GroupedList>
      <ListRow icon="question" title="Where to paste them" sub={hostLabel} onClick={onHosts} />
      {onSend ? <ListRow icon="send" title="Someone else runs your website?" sub={sentTo ? `Steps sent to ${sentTo}` : "Send the steps"} onClick={onSend} /> : null}
    </GroupedList>
  );
}

type Sample = { type: string; name: string; value: string };

/** The registrar's steps: numbered, then the three boxes of its own form filled from one record. */
export function HostSteps({ host, root, sample }: { host: HostKey; root: string; sample: Sample | undefined }) {
  const h = HOSTS[host];
  return (
    <>
      <ol className="st-steps">
        {h.steps(root).map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {sample ? (
        <>
          <h3 className="st-sh3">On {host === "other" ? "your host" : h.label}</h3>
          <ul className="k-gl k-kv">
            {[
              [h.fields[0], sample.type],
              [h.fields[1], sample.name],
              [h.fields[2], sample.value],
            ].map(([k, v]) => (
              <li key={k} className="k-lr">
                <div className="k-lr-b">
                  <span className="k-k">{k}</span>
                  <span className="k-v st-brk">{v}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {h.tip ? (
        <p className="st-tip">
          <Icon name="info" />
          <span>{h.tip.replace("{root}", root)}</span>
        </p>
      ) : null}
    </>
  );
}

/** Pick where the address was bought, then see that registrar's steps in the same sheet. */
export function HostSheet({
  open,
  onClose,
  host,
  detected,
  onHost,
  root,
  sample,
}: {
  open: boolean;
  onClose: () => void;
  host: HostKey;
  detected: HostKey | null;
  onHost: (h: HostKey) => void;
  root: string;
  sample: Sample | undefined;
}) {
  const [picked, setPicked] = useState(false);
  useEffect(() => {
    if (open) setPicked(false);
  }, [open]);
  if (!picked) {
    return (
      <Sheet open={open} onClose={onClose} title="Where did you buy your address?">
        <GroupedList noLead>
          {HOST_ORDER.map((k) => (
            <ListRow
              key={k}
              title={HOSTS[k].label}
              end={k === detected ? <span className="tag">yours</span> : undefined}
              onClick={() => {
                onHost(k);
                setPicked(true);
              }}
            />
          ))}
        </GroupedList>
      </Sheet>
    );
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={HOSTS[host].label}
      footer={
        <>
          <Button onClick={() => setPicked(false)}>Pick another host</Button>
          <Button variant="p" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <HostSteps host={host} root={root} sample={sample} />
    </Sheet>
  );
}

/** The record every registrar's form has the same three boxes for: a TXT row when there is one. */
export function sampleRecord(records: Array<{ type: string; name: string; value: string }>, nameOf: (name: string) => string): Sample | undefined {
  const r = records.find((x) => x.type.toUpperCase() === "TXT") ?? records[0];
  return r ? { type: r.type, name: nameOf(r.name), value: r.value } : undefined;
}

/** Where the records are pasted: the registrar picked, and the two sheets that open from the records page. */
export function usePaste(data: SettingsResponse) {
  const [host, setHost] = useState<HostKey>(data.sending.dns_host ?? "other");
  const [hosts, setHosts] = useState(false);
  const [steps, setSteps] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  return { host, setHost, hosts, setHosts, steps, setSteps, sentTo, setSentTo };
}

export function PasteSheets({ paste, data, root }: { paste: ReturnType<typeof usePaste>; data: SettingsResponse; root: string }) {
  const s = data.sending;
  return (
    <>
      <HostSheet
        open={paste.hosts}
        onClose={() => paste.setHosts(false)}
        host={paste.host}
        detected={s.dns_host}
        onHost={paste.setHost}
        root={root}
        sample={sampleRecord(s.records, (n) => recordName(n, root))}
      />
      <SendSteps open={paste.steps} onClose={() => paste.setSteps(false)} data={data} root={root} onSent={paste.setSentTo} />
    </>
  );
}
